import { ArticleGroup } from '../../models/rest/article-groups';
import { SizingGroup, SizingGroupKey, CgGroupingLevel } from '../../models/sizing-group';
import { Article, CommodityGroup } from '../../models/sizing-data';

/**
 * Article Group Adapter
 *
 * Transforms REST API article group responses (snake_case) to MobX store format (camelCase).
 *
 * Naming convention: mapRest{Type}ToStore{Type}
 *
 * These adapters primarily transform data. Group mapping also hydrates the matched
 * BHOS article instances with group-level fields so existing article-based selectors
 * stay in sync without replacing the store article collection.
 */

/**
 * Maps REST article groups array to store SizingGroup format
 *
 * This transformation:
 * - Converts array of REST groups to array of store groups
 * - Transforms each group individually
 * - Handles nested article transformations
 *
 * @param restGroups REST API article groups
 * @param cgLevel CG grouping level for the order
 * @param articles Original articles from BHOS (to match with REST article references)
 * @returns Array of SizingGroup in store format
 */
export function mapRestGroupsToStoreGroups(
  restGroups: ArticleGroup[],
  cgLevel: CgGroupingLevel,
  articles: Article[]
): SizingGroup[] {
  return restGroups.map((restGroup) => mapRestGroupToStoreGroup(restGroup, cgLevel, articles));
}

/**
 * Maps single REST article group to store SizingGroup format
 *
 * Transformations:
 * - key → groupKey
 * - commodity_group_code → commodityGroupCode
 * - size_structure → sizeStructure
 * - sizing_note → sizingNote
 * - reference_brand_code → referenceBrandCode
 * - is_reviewed → isReviewed
 * - Articles matched from BHOS by reference_id
 *
 * @param restGroup REST API article group
 * @param cgLevel CG grouping level
 * @param articles Original articles from BHOS
 * @returns SizingGroup in store format
 */
function mapRestGroupToStoreGroup(restGroup: ArticleGroup, cgLevel: CgGroupingLevel, articles: Article[]): SizingGroup {
  // Match articles from BHOS using reference_id from REST response
  const referenceIds = new Set(restGroup.articles.map((a) => a.reference_id).filter(Boolean));
  const groupArticles = articles
    .filter((article) => referenceIds.has(article.referenceId))
    .map((article) => {
      // Keep article-level attributes in sync with group-level values so sidebar selectors preselect correctly.
      article.sizeStructure = restGroup.size_structure ?? undefined;
      article.sizingNote = restGroup.sizing_note ?? undefined;
      article.silo = restGroup.silo ?? undefined;
      return article;
    });

  // Use commodity group from matched articles (has real names from BHOS)
  // Fall back to parsing from code if no articles matched
  const commodityGroup =
    groupArticles.length > 0
      ? groupArticles[0].commodityGroup
      : parseCommodityGroupCode(restGroup.commodity_group_code);

  // Create SizingGroup using the existing constructor
  const group = new SizingGroup(
    restGroup.key, // id
    restGroup.key, // groupKey
    cgLevel,
    commodityGroup,
    restGroup.size_structure || '',
    restGroup.sizing_note || '',
    groupArticles,
    undefined, // selectedSizeChart - will be loaded later
    undefined, // selectedSizeKey - will be loaded later
    undefined, // sizing - will be loaded later
    restGroup.reused_decision_id ? { selectedDecisionId: restGroup.reused_decision_id, isAutoReused: true } : undefined
  );

  // Set additional properties from REST group
  group.silo = restGroup.silo || undefined;
  group.sizeChartId = restGroup.size_chart_code || undefined;
  group.isReviewed = restGroup.is_reviewed;
  group.referenceBrandCode = restGroup.reference_brand_code || undefined;
  group.sizeList = restGroup.size_list;
  group.enableBox = restGroup.box_restriction_id != null;
  group.boxRestriction = {
    boxRestrictionId:
      restGroup.box_restriction_id === null || restGroup.box_restriction_id === undefined
        ? undefined
        : String(restGroup.box_restriction_id),
    boxRestrictionType: undefined,
    lastUpdated: undefined,
    boxRestrictionStatus: undefined,
  };

  return group;
}

/**
 * Parses commodity group code string into CommodityGroup structure
 *
 * Example: "2-2-G-6-01" →
 * {
 *   code: "2-2-G-6-01",
 *   codes: ["2", "2-2", "2-2-G", "2-2-G-6", "2-2-G-6-01"],
 *   names: [...] // Would need to be fetched from backend or mapped
 * }
 *
 * @param cgCode Commodity group code (e.g., "2-2-G-6-01")
 * @returns CommodityGroup structure
 */
function parseCommodityGroupCode(cgCode: string): CommodityGroup {
  const parts = cgCode.split('-');
  const codes: string[] = [];
  const names: string[] = [];

  // Build hierarchical codes array
  for (let i = 1; i <= parts.length; i++) {
    const code = parts.slice(0, i).join('-');
    codes.push(code);
    names.push(code); // Placeholder - real names would come from backend
  }

  return {
    code: cgCode,
    codes,
    names,
  };
}

/**
 * Creates SizingGroupKey from REST article group
 *
 * Used for group identification and caching
 *
 * @param restGroup REST API article group
 * @returns SizingGroupKey
 */
export function mapRestGroupToGroupKey(restGroup: ArticleGroup): SizingGroupKey {
  return {
    groupKey: restGroup.key,
    commodityGroupCode: restGroup.commodity_group_code,
    preselectedSizeChartId: restGroup.size_chart_code || undefined,
    sizeStructure: restGroup.size_structure || undefined,
    sizingNote: restGroup.sizing_note || undefined,
  };
}
